import { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { API } from "../lib/api";

export default function EnrollResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Missing reset token. Request a new link.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/enroll/reset-password`, { token, password });
      setDone(true);
      toast.success("Password reset. You can now sign in.");
      setTimeout(() => nav("/enroll/login"), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-[#FAF7EE] min-h-screen" data-testid="reset-password-page">
      <div className="border-b border-[#0F2A1F]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center">
          <Link to="/enroll" className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-[#0F2A1F] text-white flex items-center justify-center font-bold text-sm">DH</div>
            <div className="leading-tight">
              <div className="font-serif text-lg text-[#0F2A1F]">DirectHire</div>
              <div className="text-[10px] tracking-[0.25em] text-[#0F2A1F]/55 uppercase">Enrollment</div>
            </div>
          </Link>
        </div>
      </div>
      <section className="py-16 md:py-24">
        <div className="max-w-md mx-auto px-6">
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#0F2A1F]/55">Reset password</div>
          <h1 className="mt-3 text-4xl md:text-5xl font-bold text-[#0F2A1F] leading-tight">Set a new password</h1>
          {done ? (
            <div className="mt-10 rounded-xl bg-white p-8 ring-1 ring-[#0F2A1F]/10 text-center" data-testid="reset-success-card">
              <div className="text-xl font-bold text-[#0F2A1F]">Password updated</div>
              <p className="mt-3 text-sm text-[#0F2A1F]/65">Redirecting to sign in…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 rounded-xl bg-white p-8 ring-1 ring-[#0F2A1F]/10 space-y-5" data-testid="reset-form">
              <div>
                <Label className="text-[#0F2A1F]">New password</Label>
                <div className="mt-2 relative">
                  <Input type={showPw ? "text" : "password"} data-testid="reset-password-input" className="bg-[#FAF7EE] border-[#0F2A1F]/15 focus-visible:ring-[#0F2A1F] pr-10" value={password} onChange={(e) => setPassword(e.target.value)} />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0F2A1F]/50 hover:text-[#0F2A1F]" data-testid="reset-password-toggle" aria-label="Toggle password visibility">
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="mt-1.5 text-xs text-[#0F2A1F]/50">Minimum 6 characters.</p>
              </div>
              <button type="submit" disabled={busy || !token} className="w-full rounded-md bg-[#0F2A1F] text-white py-3 text-sm font-medium hover:bg-[#163b2c] disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="reset-submit-btn">
                {busy ? "Updating…" : (<>Update password <ArrowRight className="h-4 w-4" /></>)}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
